// Run view_enc + spec_mod and unpack into per-Gaussian normals + spec_vis.
//
// All per-Gaussian buffers (`specIn`, `specVis`, `normals`, `fDecPad`,
// `baseNormal`, `centroid`) live on the HairData passed in; this function
// mutates them in place — no allocations on the hot path.
//
// `camPos` is expected in splat-local (PLY authored) frame so the view
// direction stays consistent with positions / centroid (see
// geom.camPosInSplatLocal).

import { activeSpecGrid } from './specGrid.js';

const VIEW_FEAT_DIM = 8;
const SPEC_OUT_CH = 4;

// Unpack spec_mod's (1, 4, sPad, nPad) output into per-Gaussian spec_vis +
// normals: spec_vis = sigmoid(out[0,s,n]); d_n = out[1..3,s,n]; normal =
// normalize(base_normal + d_n) (falling back to base_normal when the residual
// exactly cancels it, so the cube sampler never sees a zero vector → NaN).
// Exported + pure so the WGSL shade's inline unpack (wgsl/shade.wgsl) can be
// parity-tested against it — the same contract-locking split as unpackMatMod.
// Mutates specVis/normals in place; no allocations.
export function unpackSpecMod(out, { P, N, nPad, plane }, baseNormal, { specVis, normals }) {
  for (let i = 0; i < P; i++) {
    const s = (i / N) | 0;
    const n = i % N;
    const baseIdx = s * nPad + n;
    const sv = out[0 * plane + baseIdx];
    specVis[i] = 1 / (1 + Math.exp(-sv));
    const dnx = out[1 * plane + baseIdx];
    const dny = out[2 * plane + baseIdx];
    const dnz = out[3 * plane + baseIdx];
    const nx = baseNormal[i * 3]     + dnx;
    const ny = baseNormal[i * 3 + 1] + dny;
    const nz = baseNormal[i * 3 + 2] + dnz;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 1e-8) {
      const inv = 1 / len;
      normals[i * 3]     = nx * inv;
      normals[i * 3 + 1] = ny * inv;
      normals[i * 3 + 2] = nz * inv;
    } else {
      normals[i * 3]     = baseNormal[i * 3];
      normals[i * 3 + 1] = baseNormal[i * 3 + 1];
      normals[i * 3 + 2] = baseNormal[i * 3 + 2];
    }
  }
}

export async function runSpecMod({ runner, data, camPos }) {
  const { fDecPad, baseNormal, specIn, specVis, normals, centroid, P, N } = data;
  // The grid spec_mod runs on: the tight active grid when the pack's spec_mod
  // takes dynamic dims, else the padded one. The resident input buffer (filled by
  // the WGSL mat_mod unpack), the view tiler and the WGSL shade all key off this
  // SAME (sG,nG) — HairRenderer._unpackMatModGpu sizes the buffer from it, so a
  // mismatch here trips _ensureSpecInBuffer's dims check rather than corrupting.
  const { sG, nG } = activeSpecGrid(data, runner.specCropActive);
  const plane = sG * nG;

  // Camera direction relative to hair centroid, in splat-local space.
  const dx = camPos.x - centroid.x;
  const dy = camPos.y - centroid.y;
  const dz = camPos.z - centroid.z;
  const l = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  const viewDir = new Float32Array(3);
  viewDir[0] = dx / l; viewDir[1] = dy / l; viewDir[2] = dz / l;

  // Stage timing is always-on — it's a handful of performance.now() calls
  // (NOT per-Gaussian), negligible next to a multi-hundred-ms shade, and the
  // breakdown is how we know where the time actually goes.
  // view_enc → (8,).
  const tViewEnc = performance.now();
  const viewFeat = await runner.runViewEnc(viewDir);
  const viewEncMs = performance.now() - tViewEnc;

  // Build spec_mod input: first 32 channels = cached f_dec_pad,
  // next 8 channels = view_feat broadcast.
  //
  // When the runner keeps the input gpu-buffer resident (spec_mod on WebGPU),
  // f_dec lives on the GPU after the first frame and only the view channels are
  // re-uploaded — so skip the ~268 MB CPU recopy of f_dec into specIn here too.
  // The first frame (specStaticReady === false) still builds the full tensor so
  // the static channels land on the GPU once.
  if (!(runner.specGpuIo && runner.specStaticReady)) {
    specIn.set(fDecPad, 0);
  }
  // With on-GPU view tiling the runner fills the view planes from viewFeat, so
  // skip the CPU view-broadcast (another ~67 MB memset) once the buffer is resident.
  if (!(runner.specViewTile && runner.specStaticReady)) {
    for (let c = 0; c < VIEW_FEAT_DIM; c++) {
      const v = viewFeat[c];
      const off = (32 + c) * plane;
      specIn.fill(v, off, off + plane);
    }
  }

  // Run spec_mod.
  const cIn = 32 + VIEW_FEAT_DIM;
  const tSpecMod = performance.now();
  const res = await runner.runSpecMod(specIn, sG, nG, cIn, viewFeat);
  const specModMs = performance.now() - tSpecMod;
  if (res.dims.length !== 4 || res.dims[1] !== SPEC_OUT_CH) {
    // Throw (don't warn+return): a silent return here left shadeFrame's gpushade
    // path with no _specGpuBuffer — the CPU shade then ran on stale inputs but
    // its pack was skipped, so the splat kept raw PLY colors with no signal.
    // Throwing routes to shadeFrame's catch → the loud missing-data popup.
    throw new Error(`unexpected spec_mod output dims: ${res.dims.join('x')} (asset=${runner.specAsset ?? '?'})`);
  }

  // Resident + on-GPU view tiling: after the cold full upload, the ~335 MB
  // specIn staging tensor and the ~268 MB fDecPad it is (re)built from are
  // never read again — the view planes come from an 8-float uniform. Release
  // both so the default GPU path doesn't pin ~600 MB of dead JS heap. (The
  // resident-without-tile path still partial-uploads from specIn each frame,
  // so this is gated on specViewTile.)
  if (runner.specViewTile && runner.specStaticReady && data.specIn) {
    data.specIn = null;
    data.fDecPad = null;
  }

  // gpushade: spec_out stays on the GPU; the WGSL shade pass does the unpack +
  // shade. Stash the buffer + leave the CPU normals/specVis untouched.
  if (runner.gpuShade) {
    data._specGpuBuffer = res.gpuBuffer;
    data._specPlane = plane;
    data._perfSample = { viewEnc: viewEncMs, specMod: specModMs, unpack: 0 };
    return;
  }

  const out = res.specOut;
  const tUnpack = performance.now();
  // spec_out is laid out on the grid spec_mod ran on — (sG,nG), which collapses to
  // (sPad,nPad) whenever the crop is off (i.e. always on this CPU path today).
  unpackSpecMod(out, { P, N, nPad: nG, plane }, baseNormal, { specVis, normals });

  // Stash sub-timings for shadeFrame to fold into the per-frame perf sample.
  data._perfSample = {
    viewEnc: viewEncMs,
    specMod: specModMs,
    unpack: performance.now() - tUnpack,
  };
}
